/**
 * Marketplace Configuration
 *
 * Category filtering, display utilities, and listing conversion.
 * Integration listing data comes from Galactus -- this file provides
 * client-side enrichment only.
 */

import {
   IntegrationListingResponse,
   IntegrationListingStatus,
   IntegrationTag,
   MarketplaceFilter,
   UnifiedIntegration,
} from "@/types/integrations";
import { MOMENTUM_PROVIDER } from "@/types/momentum";

/**
 * Maps backend tags that don't have their own sidebar category to the
 * sidebar category they belong under. Keeps filtering consistent without
 * needing to change backend tag data.
 */
const TAG_ALIASES: Partial<Record<IntegrationTag, IntegrationTag>> = {
   [IntegrationTag.Communications]: IntegrationTag.Telephony,
};

export function resolveTag(tag: IntegrationTag): IntegrationTag {
   return TAG_ALIASES[tag] ?? tag;
}

/**
 * Returns true if the integration should appear under the given filter.
 * Accounts for tag aliases (e.g. `communications` items appear under
 * the `telephony` / "Communications" sidebar category).
 */
export function matchesFilter(
   integration: UnifiedIntegration,
   filter: MarketplaceFilter
): boolean {
   if (resolveTag(integration.category as IntegrationTag) === filter) return true;
   return integration.tags.some((tag) => resolveTag(tag) === filter);
}

// ============================================================================
// Normalization Utilities
// ============================================================================

export const normalize = (value: string): string => value.toLowerCase().trim();
export const normalizeAlphaNum = (value: string): string =>
   normalize(value).replaceAll(/[^a-z0-9]/g, "");

/** Connect and disconnect must agree on which listing is Momentum, whatever its casing. */
export const isMomentumIntegrationKey = (key: string): boolean =>
   normalizeAlphaNum(key) === MOMENTUM_PROVIDER;

// ============================================================================
// Listing -> UnifiedIntegration Conversion
// ============================================================================

/**
 * Resolves a single description string from the localized descriptions map.
 * Tries the user's locale first, then falls back to English.
 */
export function resolveDescription(
   descriptions: Record<string, string>,
   locale?: string
): string {
   if (locale && descriptions[locale]) return descriptions[locale];
   return descriptions["en"] || Object.values(descriptions)[0] || "";
}

/**
 * Converts a Galactus listing into a UI-friendly UnifiedIntegration.
 * The first tag determines the primary category.
 */

export function toUnifiedIntegration(
   listing: IntegrationListingResponse,
   connectedIds: Set<string>,
   locale?: string
): UnifiedIntegration {
   return {
      id: listing.id,
      key: listing.key,
      name: listing.name,
      category: listing.tags[0],
      description: resolveDescription(listing.descriptions, locale),
      logoUrl: listing.logoUrl,
      status: listing.status,
      progressStatus: listing.progressStatus,
      connected:
         connectedIds.has(listing.id) ||
         connectedIds.has(listing.key) ||
         connectedIds.has(normalizeAlphaNum(listing.key)),
      connectionAvailable: listing.status === IntegrationListingStatus.Supported,
      route: listing.route ?? undefined,
      featured: listing.featured,
      tags: listing.tags,
   };
}

// ============================================================================
// Display Name Utilities
// ============================================================================

const DISPLAY_NAMES: Record<string, string> = {
   agencyzoom: "AgencyZoom",
   momentum: "Momentum (NowCerts)",
   hawksoft: "HawkSoft",
   ams360: "AMS360",
   calendly: "Calendly",
   "cal.com": "Cal.com",
   calcom: "Cal.com",
   zapier: "Zapier",
};

export function getIntegrationDisplayName(name: string): string {
   const normalized = name.toLowerCase().trim();
   return DISPLAY_NAMES[normalized] || name.charAt(0).toUpperCase() + name.slice(1);
}
